import {
  IconAnalytics,
  IconBell,
  IconBellGray,
  IconBinocularsGray,
  IconChainLink,
  IconChainLinkGray,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconClockGray,
  IconCloudCheckGray,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconPenSquare,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconShareGray,
  IconTagGray,
  IconTvocGray,
  IconWarning,
  IconZoneGray,
} from '@assets/images/icon';
import { rtlsSystem } from '@assets/images/solution';
import video from '@src/assets/images/solution/retailOffice/video/rtlsVideo.mp4';

import subVideo1 from '@src/assets/images/solution/retailOffice/video/sub/rtlsVideo1.mp4';
import subVideo2 from '@src/assets/images/solution/retailOffice/video/sub/rtlsVideo2.mp4';
import subVideo3 from '@src/assets/images/solution/retailOffice/video/sub/rtlsVideo3.mp4';

import image1 from '@src/assets/images/solution/retailMobile/img/1.avif';
import image2 from '@src/assets/images/solution/retailMobile/img/2.avif';
import image3 from '@src/assets/images/solution/retailMobile/img/3.avif';
import image4 from '@src/assets/images/solution/retailMobile/img/4.avif';
import image5 from '@src/assets/images/solution/retailMobile/img/5.avif';
import image6 from '@src/assets/images/solution/retailMobile/img/6.avif';
import image7 from '@src/assets/images/solution/retailMobile/img/7.avif';
import image8 from '@src/assets/images/solution/retailMobile/img/8.avif';
import { recommendationEdge, recommendationSwitch, retailMobile1, retailMobile2 } from '@assets/images/solution/recommendation';
import recommendation from '../common/recommendation';

export default {
  type: 'Retail Solution',
  banner: {
    title: 'Free Smartphone Experience, Automatic Display Security',
    desc: 'Offer a free smartphone experience using wireless tags while automatically maintaining display security.',
  },

  recommend: {
    title: '',
    desc: 'ORBRO OS is a powerful platform designed for real‑time location tracking and smart monitoring.<br/>It fully integrates BLE, UWB, and AI‑based video analytics to ensure safety, efficiency, and scalability in industrial environments.',
    labels: {
      desktop: [
        {
          img: retailMobile1,
          data: recommendation.safeTag,
        },
        {
          img: retailMobile2,
          data: recommendation.safeCradle,
        },
        {
          img: recommendationEdge,
          data: recommendation.edgeRtls,
        },
        {
          img: recommendationSwitch,
          data: recommendation.switch,
        },
      ],
      mobile: [
        {
          data: recommendation.safeTag,
        },
        {
          data: recommendation.safeCradle,
        },
        {
          data: recommendation.edgeRtls,
        },
        {
          data: recommendation.switch,
        },
      ],
    },
  },

  func: [
    {
      theme: 'Create a smart exhibition environment aligned with customer experience flow',
      title: 'Freely Interactive Mobile Store',
      desc: 'Customers can freely pick up and interact with smartphones, experiencing them closely without wired security. Designed to offer realistic usage without constraints.',
      sub1: 'Real‑time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image1,
    },
    {
      title: 'Signage‑linked information display',
      desc: 'When customers lift a device, its info automatically appears on signage. This allows them to learn features effortlessly, and store operators can deliver experience and info simultaneously without staff.',
      sub1: 'Real‑time status',
      icon1: IconClockGray,
      sub2: 'Fast data sync',
      icon2: IconShareGray,
      image: image2,
    },
    {
      title: 'Alert on unauthorized removal',
      desc: 'If a device leaves its designated spot, the tag sounds an alarm and alerts the manager. This ensures security without disrupting user experience.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image3,
    },
    {
      title: 'Analyze preferences from usage data',
      desc: 'Pickup counts and comparison logs are auto‑stored. Analyze usage trends to identify popular models and optimize product placement and marketing.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image4,
    },
    {
      theme: 'Special tags & cradles for device display',
      title: 'Wireless tag for free interaction',
      desc: 'The Safe Tag attaches to a device for power and location tracking. It secures devices wirelessly so customers can freely interact without restriction.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image5,
    },
    {
      title: 'Universal design for all smartphones',
      desc: 'Safe Tag fits on most smartphones regardless of manufacturer or model. One tag supports multiple devices uniformly, simplifying operations.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image6,
    },
    {
      title: 'Cradle delivering display + security',
      desc: 'Cradles power the device and enable real‑time location detection. They support wireless charging while enforcing security boundaries for stable and efficient displays.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image7,
    },
    {
      title: 'Ergonomic 15° docking angle',
      desc: 'Cradles are designed at a 15° angle, allowing natural pickup and placement. Even with repeated interactions, the device docks reliably, maintaining consistent user experience.',
      sub1: 'Analytics data',
      icon1: IconChartBarGray,
      sub2: 'Cloud data storage',
      icon2: IconCloudCheckGray,
      image: image8,
    },
  ],

  subfunc: {
    title: 'Feature Summary',
    desc: 'Provides efficient inventory control, cost and time savings, transparency, traceability, and more.<br/>To create optimal working conditions and high efficiency, ORBRO also offers the following features.',
    data: [
      {
        icon: IconLayers,
        title: 'Wireless‑tag display setup',
        desc: 'Tags are attached to exhibits to track location and monitor status wirelessly.',
      },
      {
        icon: IconLocation,
        title: 'Proximity‑based security',
        desc: 'Detects distance breaches between tag and device, triggering an alarm when the security boundary is crossed.',
      },
      {
        icon: IconSearch,
        title: 'Integrated tag attachment',
        desc: 'Tags fit neatly under devices without affecting exhibit appearance.',
      },
      {
        icon: IconAnalytics,
        title: 'Unauthorized‑removal alerts',
        desc: 'Tags sound alarms and notify managers upon departure from designated zones.',
      },
      {
        icon: IconChartBar,
        title: 'Real‑time power & location',
        desc: 'Supports free interaction during exhibition with simultaneous power supply and real‑time location tracking.',
      },
      {
        icon: IconRoute,
        title: 'Sliding-hinge structure',
        desc: 'Tags flexibly adjust to different devices depending on charging port location.',
      },
      {
        icon: IconRoute,
        title: '15° docking angle design',
        desc: 'Ergonomic angle for natural pickup and placement.',
      },
      {
        icon: IconLayers,
        title: 'Clean wireless-charging display',
        desc: 'Cradles and tags remain hidden, maintaining a clean display while providing stable power.',
      },
    ],
  },

  system: {
    title: 'System Configuration',
    img: rtlsSystem,
  },

  productTitle: 'Product Lineup',
  product: [
    {
      tag: 'assetPackage',
    },
    {
      tag: 'workerPackage',
    },
  ],

  contents: [
    {
      data: 'logisticsAsset',
    },
    {
      data: 'manufacturingSteel',
    },
    {
      data: 'blogOffice',
    },
    {
      data: 'blogRTLS',
    },
  ],
};
